import { useState, useEffect, useRef } from "react";
import { View, StyleSheet, Pressable, Image, TextInput } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { AttachmentFile } from "@/components/Attachment/types";
import { getFileIcon, getFileColor, formatFileSize } from "@/components/Attachment/lib/fileUtils";
import { supabase } from "@/lib/supabase-client";
import { ViewMode } from "../types";

// Helper to separate filename and extension
function splitFilename(filename: string): { name: string; extension: string } {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot === -1 || lastDot === 0) {
    return { name: filename, extension: "" };
  }
  return {
    name: filename.substring(0, lastDot),
    extension: filename.substring(lastDot),
  };
}

declare global {
  interface Window {
    pdfjsLib?: any;
    _pdfjsLoadPromise?: Promise<any>;
  }
}

// Use legacy build that exposes window.pdfjsLib
const PDFJS_CDN = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
const PDFJS_WORKER_CDN = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

// Load pdf.js from CDN via script tag
function loadPdfJs(): Promise<any> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve(null);
  }

  if (window.pdfjsLib) {
    return Promise.resolve(window.pdfjsLib);
  }

  if (window._pdfjsLoadPromise) {
    return window._pdfjsLoadPromise;
  }

  window._pdfjsLoadPromise = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = PDFJS_CDN;
    script.async = true;
    script.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
        resolve(window.pdfjsLib);
      } else {
        resolve(null);
      }
    };
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });

  return window._pdfjsLoadPromise;
}

// Generate PDF thumbnail from first page
async function generatePdfThumbnail(pdfUrl: string, maxSize: number = 200): Promise<string | null> {
  const pdfjs = await loadPdfJs();
  if (!pdfjs) return null;

  try {
    const pdf = await pdfjs.getDocument({ url: pdfUrl }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1 });

    // Scale to fit maxSize
    const scale = Math.min(maxSize / viewport.width, maxSize / viewport.height);
    const scaledViewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;

    await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;

    return canvas.toDataURL("image/jpeg", 0.8);
  } catch (err) {
    console.error("Failed to generate PDF thumbnail:", err);
    return null;
  }
}

interface FileItemProps {
  file: AttachmentFile;
  mode: ViewMode;
  onRemove?: () => void;
  onPreview?: () => void;
  onRename?: (newName: string) => void;
  selected?: boolean;
}

export function FileItem({ file, mode, onRemove, onPreview, onRename, selected }: FileItemProps) {
  const { colors } = useTheme();
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const inputRef = useRef<TextInput>(null);

  const isImage = file.category === "image";
  const isPdf = file.type === "application/pdf" || file.name.endsWith(".pdf");
  const icon = getFileIcon(file);
  const iconColor = getFileColor(file);
  const { name: baseName, extension } = splitFilename(file.name);

  const startEditing = (e: any) => {
    e.stopPropagation();
    setEditedName(baseName);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedName("");
  };

  const saveRename = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== baseName && onRename) {
      onRename(trimmedName + extension);
    }
    setIsEditing(false);
    setEditedName("");
  };

  const handleKeyPress = (e: any) => {
    if (e.nativeEvent.key === "Enter") {
      saveRename();
    } else if (e.nativeEvent.key === "Escape") {
      cancelEditing();
    }
  };

  // Load thumbnail for images and PDFs
  useEffect(() => {
    let cancelled = false;

    const loadThumbnail = async () => {
      try {
        let signedUrl: string | null = null;

        // Get signed URL if from storage
        if (file.bucket && file.uri) {
          const { data } = await supabase.storage.from(file.bucket).createSignedUrl(file.uri, 3600);
          signedUrl = data?.signedUrl || null;
        } else if (file.uri && !file.bucket) {
          signedUrl = file.uri;
        }

        if (cancelled || !signedUrl) return;

        if (isImage) {
          setThumbnailUrl(signedUrl);
        } else if (isPdf) {
          const pdfThumbnail = await generatePdfThumbnail(signedUrl);
          if (!cancelled && pdfThumbnail) {
            setThumbnailUrl(pdfThumbnail);
          }
        }
      } catch (err) {
        console.error("Failed to load thumbnail:", err);
      }
    };

    if (isImage || isPdf) {
      loadThumbnail();
    }

    return () => {
      cancelled = true;
    };
  }, [file.uri, file.bucket, isImage, isPdf]);

  if (mode === "icons") {
    return (
      <Pressable
        onPress={isEditing ? undefined : onPreview}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        style={[styles.iconItem, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}
      >
        {/* Thumbnail or Icon */}
        <View style={styles.iconThumbnail}>
          {thumbnailUrl ? (
            <Image source={{ uri: thumbnailUrl }} style={styles.iconImage} resizeMode="cover" />
          ) : (
            <MaterialCommunityIcons name={icon as any} size={48} color={iconColor} />
          )}
        </View>

        {/* Filename or Edit Input */}
        {isEditing ? (
          <View style={styles.iconEditContainer}>
            <TextInput
              ref={inputRef}
              value={editedName}
              onChangeText={setEditedName}
              onBlur={saveRename}
              onKeyPress={handleKeyPress}
              style={[styles.iconEditInput, { color: colors.text, borderColor: colors.primary }]}
              selectTextOnFocus
            />
            <ThemedText style={[styles.iconExtension, { color: colors.textSecondary }]}>{extension}</ThemedText>
          </View>
        ) : (
          <ThemedText numberOfLines={2} style={styles.iconFilename}>
            {file.name}
          </ThemedText>
        )}

        {/* Action buttons on hover */}
        {hovered && !isEditing && (onRename || onRemove) && (
          <View style={styles.iconActionButtons}>
            {onRename && (
              <Pressable
                onPress={startEditing}
                style={[styles.iconActionButton, { backgroundColor: colors.backgroundSecondary }]}
              >
                <MaterialCommunityIcons name="pencil" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
            {onRemove && (
              <Pressable
                onPress={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                style={[styles.iconActionButton, { backgroundColor: colors.red.background }]}
              >
                <MaterialCommunityIcons name="close" size={14} color={colors.red.text} />
              </Pressable>
            )}
          </View>
        )}
      </Pressable>
    );
  }

  if (mode === "list") {
    return (
      <Pressable
        onPress={isEditing ? undefined : onPreview}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        style={[
          styles.listItem,
          { backgroundColor: hovered ? colors.backgroundSecondary : "transparent", borderColor: colors.border },
        ]}
      >
        {/* Icon - only show image thumbnails in list view, not PDF */}
        <View style={[styles.listIcon, { backgroundColor: colors.backgroundSecondary }]}>
          {isImage && thumbnailUrl ? (
            <Image source={{ uri: thumbnailUrl }} style={styles.listImage} resizeMode="cover" />
          ) : (
            <MaterialCommunityIcons name={icon as any} size={24} color={iconColor} />
          )}
        </View>

        {/* Filename or Edit Input */}
        {isEditing ? (
          <View style={styles.listEditContainer}>
            <TextInput
              ref={inputRef}
              value={editedName}
              onChangeText={setEditedName}
              onBlur={saveRename}
              onKeyPress={handleKeyPress}
              style={[styles.listEditInput, { color: colors.text, borderColor: colors.primary }]}
              selectTextOnFocus
            />
            <ThemedText style={[styles.listExtension, { color: colors.textSecondary }]}>{extension}</ThemedText>
          </View>
        ) : (
          <View style={styles.listNameContainer}>
            <ThemedText numberOfLines={1} style={styles.listFilename}>
              {file.name}
            </ThemedText>
            {/* Edit button - next to filename */}
            {onRename && (
              <Pressable onPress={startEditing} style={[styles.listEditButton, { opacity: hovered ? 1 : 0 }]}>
                <MaterialCommunityIcons name="pencil-outline" size={16} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        )}

        {/* Size */}
        <ThemedText style={[styles.listSize, { color: colors.textSecondary }]}>{formatFileSize(file.size)}</ThemedText>

        {/* Type */}
        <ThemedText style={[styles.listType, { color: colors.textSecondary }]}>{file.category}</ThemedText>

        {/* Delete button */}
        {onRemove && (
          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            style={[styles.listDeleteButton, { opacity: hovered ? 1 : 0 }]}
          >
            <MaterialCommunityIcons name="trash-can-outline" size={18} color={colors.red.text} />
          </Pressable>
        )}
      </Pressable>
    );
  }

  // Columns mode (single column, compact)
  const columnBgColor = selected ? colors.primary + "20" : hovered ? colors.backgroundSecondary : "transparent";

  return (
    <Pressable
      onPress={isEditing ? undefined : onPreview}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      style={[
        styles.columnItem,
        { backgroundColor: columnBgColor, borderColor: colors.border },
        selected && { borderLeftWidth: 3, borderLeftColor: colors.primary },
      ]}
    >
      {/* Icon */}
      <MaterialCommunityIcons name={icon as any} size={20} color={iconColor} />

      {/* Filename or Edit Input */}
      {isEditing ? (
        <View style={styles.columnEditContainer}>
          <TextInput
            ref={inputRef}
            value={editedName}
            onChangeText={setEditedName}
            onBlur={saveRename}
            onKeyPress={handleKeyPress}
            style={[styles.columnEditInput, { color: colors.text, borderColor: colors.primary }]}
            selectTextOnFocus
          />
          <ThemedText style={[styles.columnExtension, { color: colors.textSecondary }]}>{extension}</ThemedText>
        </View>
      ) : (
        <ThemedText numberOfLines={1} style={styles.columnFilename}>
          {file.name}
        </ThemedText>
      )}

      {/* Edit button */}
      {onRename && !isEditing && (
        <Pressable onPress={startEditing} style={[styles.columnActionButton, { opacity: hovered ? 1 : 0 }]}>
          <MaterialCommunityIcons name="pencil" size={14} color={colors.textSecondary} />
        </Pressable>
      )}

      {/* Delete button */}
      {onRemove && (
        <Pressable
          onPress={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          style={[styles.columnActionButton, { opacity: hovered ? 1 : 0 }]}
        >
          <MaterialCommunityIcons name="close" size={16} color={colors.textSecondary} />
        </Pressable>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  // Icons mode styles
  iconItem: {
    width: 120,
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
    position: "relative",
  },
  iconThumbnail: {
    width: 80,
    height: 80,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
  },
  iconImage: {
    width: 80,
    height: 80,
    borderRadius: 6,
  },
  iconFilename: {
    fontSize: 12,
    textAlign: "center",
    lineHeight: 16,
  },
  iconActionButtons: {
    position: "absolute",
    top: 4,
    right: 4,
    flexDirection: "row",
    gap: 4,
  },
  iconActionButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  iconEditContainer: {
    width: "100%",
    alignItems: "center",
  },
  iconEditInput: {
    width: "100%",
    fontSize: 12,
    textAlign: "center",
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  iconExtension: {
    fontSize: 10,
    marginTop: 2,
  },

  // List mode styles
  listItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 12,
  },
  listIcon: {
    width: 40,
    height: 40,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  listImage: {
    width: 40,
    height: 40,
  },
  listNameContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  listFilename: {
    flex: 1,
    fontSize: 14,
  },
  listEditButton: {
    padding: 2,
  },
  listSize: {
    fontSize: 12,
    width: 80,
    textAlign: "right",
  },
  listType: {
    fontSize: 12,
    width: 70,
    textAlign: "center",
    textTransform: "capitalize",
  },
  listDeleteButton: {
    padding: 4,
  },
  listEditContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  listEditInput: {
    flex: 1,
    fontSize: 14,
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  listExtension: {
    fontSize: 12,
  },

  // Columns mode styles
  columnItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 8,
  },
  columnFilename: {
    flex: 1,
    fontSize: 13,
  },
  columnActionButton: {
    padding: 2,
  },
  columnEditContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  columnEditInput: {
    flex: 1,
    fontSize: 13,
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  columnExtension: {
    fontSize: 11,
  },
});
